import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Box, 
    Typography, 
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Alert,
    Tooltip,
    useTheme,
    Card,
    CardContent,
    CardMedia,
    Link,
    List,
    ListItem,
    ListItemText,
    Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import { getOwnerCodes, createCode, updateCode, deleteCode } from '../../services/codeManagementApi';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import CodeForm from './CodeForm';
import { CodeHistoryItem } from '../CodeHistoryItem';
import CodeManagerDashboard from './CodeManagerDashboard';
import { useNavigate } from 'react-router-dom';

const CODE_FORMAT_REGEX = /^[A-Za-z]{2,3}[0-9]{4}$/;

const validateCodeFormat = (code) => {
    return CODE_FORMAT_REGEX.test(code);
};

const formatDateTime = (date) => {
    return date ? format(new Date(date), 'dd-MMM-yyyy') : '-';
};

const StatusChip = ({ status }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const config = {
        ACTIVE: {
            bg: isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)',
            color: '#10B981',
            label: 'Active'
        },
        ARCHIVED: {
            bg: isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(148, 163, 184, 0.1)',
            color: '#94A3B8',
            label: 'Expired'
        },
        RESERVED: {
            bg: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 102, 255, 0.1)',
            color: theme.palette.primary.main,
            label: 'Reserved'
        }
    };
    const c = config[status] || config.ACTIVE;

    return (
        <Box
            sx={{
                backgroundColor: c.bg,
                color: c.color,
                border: `1px solid ${c.color}33`,
                px: { xs: 0.8, sm: 1.2 },
                py: { xs: 0.2, sm: 0.35 },
                borderRadius: '20px',
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                fontWeight: 700,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                width: 'fit-content'
            }}
        >
            {c.label}
        </Box>
    );
};

const CodeCard = ({ code }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    return (
        <Card sx={{ 
            display: 'flex', 
            width: '100%',
            minWidth: 0,
            backgroundColor: isDark ? '#182235' : '#F1F5F9',
            borderRadius: '14px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            overflow: 'hidden'
        }}>
            <CardMedia
                component="img"
                sx={{ 
                    width: 60, 
                    height: 60,
                    objectFit: 'contain',
                    p: 1,
                    flexShrink: 0
                }}
                image={code.image_url || '/illustrations/buildboard01.png'}
                alt={code.code_name}
            />
            <CardContent sx={{ 
                flex: 1,
                minWidth: 0,
                py: 1,
                px: 2,
                '&:last-child': { pb: 1 },
                overflow: 'hidden'
            }}>
                <Typography 
                    variant="subtitle1" 
                    component="div" 
                    sx={{ 
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {code.code_value}
                </Typography>
                <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                        mb: 0.5,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {code.code_name}
                </Typography>
                <Typography 
                    variant="caption" 
                    color="text.tertiary"
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block'
                    }}
                >
                    {code.code_description}
                </Typography>
            </CardContent>
        </Card>
    );
};

const ResizableColumn = ({ children, width, onResize }) => {
    const [isResizing, setIsResizing] = useState(false);
    const [initialX, setInitialX] = useState(0);
    const [initialWidth, setInitialWidth] = useState(width);
    const theme = useTheme();

    const startResize = useCallback((e) => {
        e.preventDefault();
        setIsResizing(true);
        setInitialX(e.clientX);
        setInitialWidth(width);
    }, [width]);

    const stopResize = useCallback(() => {
        setIsResizing(false);
    }, []);

    const resize = useCallback((e) => {
        if (isResizing) {
            const diff = e.clientX - initialX;
            const newWidth = Math.max(100, initialWidth + diff);
            onResize(newWidth);
        }
    }, [isResizing, initialX, initialWidth, onResize]);

    useEffect(() => {
        if (isResizing) {
            document.body.style.cursor = 'col-resize';
            window.addEventListener('mousemove', resize);
            window.addEventListener('mouseup', stopResize);
        } else {
            document.body.style.cursor = 'default';
        }

        return () => {
            document.body.style.cursor = 'default';
            window.removeEventListener('mousemove', resize);
            window.removeEventListener('mouseup', stopResize);
        };
    }, [isResizing, resize, stopResize]);

    return (
        <TableCell 
            sx={{ 
                position: 'relative',
                width: width,
                minWidth: width,
                userSelect: isResizing ? 'none' : 'auto',
                borderRight: `1px solid ${theme.palette.table.border}`,
                p: 2,
                '&:hover .resizer': {
                    backgroundColor: theme.palette.table.resizer.hover,
                    opacity: 0.5,
                }
            }}
        >
            {children}
            <Box
                className="resizer"
                sx={{
                    position: 'absolute',
                    right: -3,
                    top: 0,
                    bottom: 0,
                    width: '6px',
                    cursor: 'col-resize',
                    backgroundColor: isResizing 
                        ? theme.palette.table.resizer.hover 
                        : theme.palette.table.resizer.default,
                    opacity: isResizing ? 0.8 : 0.2,
                    transition: 'opacity 0.2s, background-color 0.2s',
                    zIndex: 1000,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '2px',
                        height: '20px',
                        backgroundColor: isResizing 
                            ? theme.palette.table.resizer.active 
                            : theme.palette.table.resizer.default,
                    }
                }}
                onMouseDown={startResize}
            />
        </TableCell>
    );
};

const addImageTimestamp = (codes) => {
    return codes.map(code => ({
        ...code,
        // Add timestamp to force image refresh
        imageUrl: code.image_url ? `${code.image_url}${code.image_url.includes('?') ? '&' : '?'}t=${Date.now()}` : null
    }));
};

const CodeListItem = React.memo(({ code, refreshImageCodes }) => {
    return (
        <CodeHistoryItem 
            code={{
                code: code.code_value,
                codeName: code.code_name,
                codeDescription: code.code_description,
                imageUrl: code.image_url && refreshImageCodes.has(code.code_value) 
                    ? `${code.image_url}${code.image_url.includes('?') ? '&' : '?'}t=${Date.now()}`
                    : code.image_url,
                url: code.code_url
            }}
            onHistoryItemClick={() => {}}
            showArrow={false}
        />
    );
});

const CodeManager = ({ ownerData, onLogout }) => {
    const containerRef = useRef(null);
    const [codes, setCodes] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [overlappingCodes, setOverlappingCodes] = useState([]);
    
    // Initialize column widths with null values
    const [columnWidths, setColumnWidths] = useState({
        details: null,    // 50%
        status: null,     // 35%
        actions: null     // 15%
    });

    // Add state to track which codes need fresh images
    const [refreshImageCodes, setRefreshImageCodes] = useState(new Set());
    
    // Add state to track initial load
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Add state for delete dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [codeToDelete, setCodeToDelete] = useState(null);

    // Add state for forcing dashboard refresh
    const [dashboardRefreshTrigger, setDashboardRefreshTrigger] = useState(Date.now());

    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const navigate = useNavigate();
    const [activeCodesCount, setActiveCodesCount] = useState(0);

    // Use existing codes data to calculate active codes count
    useEffect(() => {
        // Filter codes where deleted_at is null
        const activeCodes = codes.filter(code => !code.deleted_at);
        setActiveCodesCount(activeCodes.length);
    }, [codes]); // Update count whenever codes change

    // Add useEffect to calculate initial widths
    useEffect(() => {
        const calculateInitialWidths = () => {
            if (containerRef.current) {
                // Account for List padding and gaps between sections
                const totalPadding = 48;  // Container padding (24px * 2)
                const listItemPadding = 48;  // ListItem padding (24px * 2)
                const gapsBetweenSections = 48;  // Total gaps (24px * 2)
                const availableWidth = containerRef.current.offsetWidth - totalPadding - listItemPadding - gapsBetweenSections;

                setColumnWidths({
                    details: Math.floor(availableWidth * 0.5),
                    status: Math.floor(availableWidth * 0.35),
                    actions: Math.floor(availableWidth * 0.15)
                });
            }
        };

        calculateInitialWidths();
        window.addEventListener('resize', calculateInitialWidths);
        return () => window.removeEventListener('resize', calculateInitialWidths);
    }, []);

    const fetchCodes = useCallback(async () => {
        try {
            const response = await getOwnerCodes();
            setCodes(response);
        } catch (error) {
            console.error('Error fetching codes:', error);
        }
    }, [ownerData]);

    useEffect(() => {
        fetchCodes();
    }, [fetchCodes]);

    useEffect(() => {
        console.log('CodeManager mounted with owner data:', ownerData);
    }, [ownerData]);

    useEffect(() => {
        // On initial load, all codes should get fresh images
        if (isInitialLoad && codes.length > 0) {
            setRefreshImageCodes(new Set(codes.map(code => code.code_value)));
            setIsInitialLoad(false);
        }
    }, [codes, isInitialLoad]);

    const handleDialogOpen = (code = null) => {
        if (code) {
            setEditingCode({
                code_id: code.code_id,
                code_value: code.code_value,
                code_name: code.code_name,
                code_description: code.code_description,
                code_url: code.code_url,
                valid_from: code.valid_from,
                valid_until: code.valid_until,
                image_url: code.image_url,
                status: code.status
            });
        } else {
            setEditingCode(null);
        }
        setOpenDialog(true);
    };

    const handleDialogClose = useCallback(() => {
        setOpenDialog(false);
        setEditingCode(null);
    }, []);

    const handleDeleteClick = (code) => {
        setCodeToDelete(code);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteCode(codeToDelete.code_id);
            setDeleteDialogOpen(false);
            setCodeToDelete(null);
            await fetchCodes();
            // Trigger dashboard refresh after delete
            setDashboardRefreshTrigger(Date.now());
        } catch (error) {
            console.error('Failed to delete code:', error);
        }
    };

    const handleColumnResize = (column, newWidth) => {
        setColumnWidths(prev => ({
            ...prev,
            [column]: newWidth
        }));
    };

    // Handle successful code save (create/edit)
    const handleCodeSaved = async () => {
        setEditingCode(null);
        setOpenDialog(false);
        
        try {
            const freshCodes = await getOwnerCodes();
            // Use the renamed function
            setCodes(addImageTimestamp(freshCodes));
        } catch (error) {
            console.error('Error refreshing codes:', error);
        }
    };

    return (
        <Box ref={containerRef} sx={{ 
            flex: 1,
            px: 0
        }}>
            
            {/* Header: Welcome & Account */}
            <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: { xs: 1.5, sm: 2 },
                mb: 3,
                borderRadius: '16px',
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'}`,
                boxShadow: isDark ? '0 4px 20px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(15, 23, 42, 0.03)',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        boxShadow: '0 2px 8px rgba(0, 102, 255, 0.35)'
                    }}>
                        {ownerData?.email ? ownerData.email.charAt(0).toUpperCase() : 'U'}
                    </Box>
                    <Box>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: theme.palette.text.secondary,
                                display: 'block',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}
                        >
                            Logged in as
                        </Typography>
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                color: theme.palette.text.primary, 
                                fontWeight: 600,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                        >
                            {ownerData?.email}
                        </Typography>
                    </Box>
                </Box>
                <Tooltip title="Log Out">
                    <Button 
                        onClick={onLogout}
                        size="small"
                        startIcon={<LogoutIcon fontSize="small" />}
                        sx={{ 
                            color: theme.palette.text.secondary,
                            borderRadius: '20px',
                            px: 1.75,
                            py: 0.6,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.12)'}`,
                            '&:hover': {
                                color: theme.palette.error.main,
                                borderColor: theme.palette.error.main,
                                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.04)'
                            }
                        }}
                    >
                        Log out
                    </Button>
                </Tooltip>
            </Box>
            
            {/* Actions Bar */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2.5, 
                width: '100%' 
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            color: theme.palette.text.primary, 
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            fontSize: { xs: '1.15rem', sm: '1.4rem' }
                        }}
                    >
                        My Codes
                    </Typography>
                    <Box sx={{
                        px: 1.25,
                        py: 0.25,
                        borderRadius: '12px',
                        backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 102, 255, 0.08)',
                        color: theme.palette.primary.main,
                        fontSize: '0.8rem',
                        fontWeight: 700
                    }}>
                        {activeCodesCount}
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    onClick={() => handleDialogOpen()}
                    startIcon={<AddIcon />}
                    sx={{
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        fontWeight: 600,
                        textTransform: 'none',
                        px: { xs: 2, sm: 3 },
                        py: 1,
                        borderRadius: '24px',
                        background: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
                        color: '#FFFFFF',
                        boxShadow: '0 4px 14px rgba(0, 102, 255, 0.35)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #0052CC 0%, #003D99 100%)',
                            boxShadow: '0 6px 20px rgba(0, 102, 255, 0.45)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Add New Code
                </Button>
            </Box>

            <List sx={{ 
                height: { 
                    xs: '300px',
                    sm: '400px'
                },
                backgroundColor: 'transparent',
                overflow: 'auto',
                width: '100%',
                p: 0,
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: theme.palette.scrollbar.track,
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.scrollbar.thumb,
                    borderRadius: '4px',
                    '&:hover': {
                        background: theme.palette.scrollbar.thumbHover,
                    },
                },
            }}>
                {codes.length === 0 ? (
                    <Box sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: '16px',
                        border: `1px dashed ${theme.palette.divider}`,
                        color: theme.palette.text.secondary
                    }}>
                        <Typography variant="body1">No codes yet. Click &quot;Add New Code&quot; to create your first code!</Typography>
                    </Box>
                ) : (
                    codes.sort((a, b) => a.code_value.localeCompare(b.code_value))
                    .map((code, index) => (
                        <ListItem
                            key={code.code_id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 1, sm: 2 },
                                py: { xs: 1, sm: 1.5 },
                                px: { xs: 1.25, sm: 2 },
                                backgroundColor: isDark ? '#131C2E' : '#FFFFFF',
                                borderRadius: '16px',
                                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'}`,
                                boxShadow: isDark ? '0 4px 16px rgba(0, 0, 0, 0.25)' : '0 2px 8px rgba(15, 23, 42, 0.04)',
                                mb: 1.5,
                                mt: index === 0 ? 0.5 : 0,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: isDark ? '#18243A' : '#F8FAFC',
                                    transform: 'translateY(-1px)',
                                    boxShadow: isDark ? '0 8px 24px rgba(0, 0, 0, 0.35)' : '0 4px 12px rgba(15, 23, 42, 0.08)',
                                },
                                boxSizing: 'border-box',
                            }}
                        >
                            {/* Code Details Card - Left side */}
                            <Box sx={{ 
                                width: { xs: '52%', sm: '48%' },
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <CodeListItem 
                                    code={code}
                                    refreshImageCodes={refreshImageCodes}
                                />
                            </Box>

                            {/* Status & Info - Middle */}
                            <Box sx={{ 
                                width: { xs: '34%', sm: '38%' },
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                minWidth: 0,
                                alignSelf: 'center'
                            }}>
                                <StatusChip status={code.status} />
                                <Typography 
                                    variant="body2" 
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                        fontWeight: 500
                                    }}
                                >
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                        {code.code_url}
                                    </Box>
                                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                        {code.code_url.replace(/(^\w+:|^)\/\/(www\.)?/, '')}
                                    </Box>
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{
                                        color: theme.palette.text.tertiary,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                >
                                    {formatDateTime(code.valid_from)} — {formatDateTime(code.valid_until)}
                                </Typography>
                            </Box>

                            {/* Actions - Right side */}
                            <Box sx={{ 
                                width: { xs: '15%', sm: '14%' },
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: { xs: 0.5, sm: 1 },
                                alignSelf: 'center'
                            }}>
                                <Tooltip title="Edit Code">
                                    <IconButton 
                                        onClick={() => handleDialogOpen(code)}
                                        disabled={code.status === 'ARCHIVED'}
                                        size="small"
                                        sx={{ 
                                            color: theme.palette.primary.main,
                                            backgroundColor: isDark ? 'rgba(0, 102, 255, 0.12)' : 'rgba(0, 102, 255, 0.08)',
                                            borderRadius: '10px',
                                            '&:hover': { 
                                                backgroundColor: isDark ? 'rgba(0, 102, 255, 0.25)' : 'rgba(0, 102, 255, 0.16)' 
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Code">
                                    <IconButton 
                                        onClick={() => handleDeleteClick(code)}
                                        size="small"
                                        sx={{ 
                                            color: theme.palette.error.main,
                                            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)',
                                            borderRadius: '10px',
                                            '&:hover': { 
                                                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.16)' 
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </ListItem>
                    ))
                )}
            </List>

            {/* Dashboard container */}
            <Box sx={{
                width: '100%',
                backgroundColor: isDark ? '#131C2E' : '#FFFFFF',
                borderRadius: '20px',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'}`,
                boxShadow: isDark ? '0 8px 24px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(15, 23, 42, 0.04)',
                mt: { xs: 3, sm: 2.5 },
                p: { xs: 1.5, sm: 2.5 },
                display: 'flex',
                flexDirection: 'column'
            }}>
                <CodeManagerDashboard 
                    codes={codes} 
                    forceRefresh={dashboardRefreshTrigger}
                />
            </Box>

            {openDialog && (
                <CodeForm 
                    open={openDialog}
                    onClose={handleDialogClose}
                    editingCode={editingCode}
                    onCodeSaved={handleCodeSaved}
                />
            )}

            {/* Delete confirmation dialog */}
            <Dialog 
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)'}`,
                        boxShadow: isDark ? '0 24px 60px rgba(0,0,0,0.7)' : '0 20px 50px rgba(15,23,42,0.15)',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Confirm Code Deletion</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                            Are you sure you want to delete this code? This action cannot be undone.
                        </Typography>
                        {codeToDelete && (
                            <CodeCard code={codeToDelete} />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ color: theme.palette.text.secondary, borderRadius: '20px', px: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                        sx={{
                            borderRadius: '20px',
                            px: 3,
                            fontWeight: 600,
                            backgroundColor: theme.palette.error.main,
                            '&:hover': {
                                backgroundColor: '#DC2626'
                            }
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

CodeManager.propTypes = {
    ownerData: PropTypes.shape({
        email: PropTypes.string,
        token: PropTypes.string.isRequired,
    }),
    onLogout: PropTypes.func.isRequired
};

export default CodeManager; 